import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { usePrivy } from '@privy-io/react-auth';
import { useMultiChainBalances, TokenBalance } from '../../hooks/useMultiChainBalances';
import { useSolanaBalances } from '../../hooks/useSolanaBalances';
import { supabase } from '../../utils/supabase';
import PortfolioChart from './PortfolioChart';

const Card = styled.div`
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 12px;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #a1a1aa;
  margin: 0;
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: #a1a1aa;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 150ms ease;
  
  &:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TotalBalance = styled.div`
  font-size: 32px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 4px;
`;

const BalanceSubtext = styled.div`
  font-size: 13px;
  color: #71717a;
  margin-bottom: 16px;
`;

const TokenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 16px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #000000;
    border-radius: 2px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 8px;
  transition: background 150ms ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const TokenIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const TokenInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TokenSymbol = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TokenChain = styled.div`
  font-size: 11px;
  color: #52525b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ContractBadge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background: rgba(39, 39, 42, 0.8);
  border: 1px solid #3f3f46;
  border-radius: 4px;
  color: #71717a;
  font-size: 10px;
  font-family: 'IBM Plex Mono', monospace;
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background: rgba(63, 63, 70, 0.8);
    color: #a1a1aa;
    border-color: #52525b;
  }
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

const CopiedTooltip = styled.span`
  position: absolute;
  top: -24px;
  left: 50%;
  transform: translateX(-50%);
  background: #10b981;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  white-space: nowrap;
`;

const ContractWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TokenValue = styled.div`
  text-align: right;
`;

const TokenUSD = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #ffffff;
`;

const TokenAmount = styled.div`
  font-size: 11px;
  color: #52525b;
`;

const FundingOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid var(--color-border, #333333);
  padding-top: 16px;
`;

const FundingOption = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--color-bg-hover, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  transition: all 150ms ease;
  text-align: left;
  
  &:hover {
    background: var(--color-bg-card, #000000);
    border-color: var(--color-border-light, #444444);
  }
`;

const FundingIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 8px;
  color: #10b981;
  font-size: 16px;
`;

const FundingText = styled.div`
  flex: 1;
`;

const FundingTitle = styled.div`
  font-weight: 500;
  margin-bottom: 2px;
`;

const FundingDesc = styled.div`
  font-size: 11px;
  color: #71717a;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #71717a;
  font-size: 13px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px 0;
  color: #52525b;
  font-size: 13px;
`;

const ConnectPrompt = styled.div`
  text-align: center;
  padding: 24px 0;
  color: #71717a;
  font-size: 13px;
  
  p {
    margin: 0;
  }
`;

// Supabase token data for logo cross-reference
interface SupabaseToken {
  contract_address: string;
  logo_url: string | null;
  name: string;
  symbol: string;
}

const formatUSD = (value: number): string => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(2)}K`;
  if (value >= 1) return `$${value.toFixed(2)}`;
  if (value > 0) return `$${value.toFixed(4)}`;
  return '$0.00';
};

const formatAmount = (amount: string, decimals: number = 4): string => {
  const num = parseFloat(amount);
  if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
  if (num >= 1) return num.toFixed(decimals);
  if (num > 0) return num.toFixed(Math.min(decimals + 2, 8));
  return '0';
};

// Format contract address as 0x...XXXX
const formatContractAddress = (address: string | null): string => {
  if (!address) return '';
  return `0x...${address.slice(-4)}`;
};

interface PortfolioPreviewProps {
  className?: string;
}

const PortfolioPreview: React.FC<PortfolioPreviewProps> = ({ className }) => {
  const navigate = useNavigate();
  const { isConnected } = useAccount();
  const { authenticated } = usePrivy();
  const { balances: evmBalances, loading: evmLoading, error: evmError } = useMultiChainBalances(isConnected);
  const { 
    address: solanaAddress, 
    balances: solanaBalances, 
    loading: solanaLoading, 
    totalValueUSD: solanaTotalUSD 
  } = useSolanaBalances(authenticated);
  const [supabaseTokens, setSupabaseTokens] = useState<Map<string, SupabaseToken>>(new Map());
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  
  // Merge EVM and Solana balances
  const balances: TokenBalance[] = useMemo(() => {
    const merged: TokenBalance[] = [...evmBalances];
    
    // Add Solana balances (convert to TokenBalance format)
    solanaBalances.forEach(solBalance => {
      merged.push({
        chainId: 792703809, // Relay's Solana chain ID
        chainName: 'Solana',
        tokenAddress: solBalance.isNative ? null : solBalance.mint as `0x${string}`,
        symbol: solBalance.symbol,
        name: solBalance.name,
        balance: solBalance.balance,
        balanceRaw: solBalance.balanceRaw,
        decimals: solBalance.decimals,
        logo: solBalance.logo,
        priceUSD: solBalance.priceUSD,
        valueUSD: solBalance.valueUSD,
      });
    });
    
    return merged;
  }, [evmBalances, solanaBalances]);
  
  const loading = evmLoading || solanaLoading;
  const error = evmError;

  // Fetch Supabase tokens for logo cross-reference
  useEffect(() => {
    const fetchSupabaseLogos = async () => {
      if (!balances || balances.length === 0) return;
      
      // Get all token addresses from balances (for Ink chain tokens)
      const inkTokenAddresses = balances
        .filter(b => b.tokenAddress && b.chainId === 57073) // Ink chain ID
        .map(b => b.tokenAddress!.toLowerCase());
      
      // Get Ethereum token addresses (chainId 1)
      const ethTokenAddresses = balances
        .filter(b => b.tokenAddress && b.chainId === 1) // Ethereum mainnet
        .map(b => b.tokenAddress!.toLowerCase());
      
      const tokenMap = new Map<string, SupabaseToken>();
      
      // Fetch Ink tokens
      if (inkTokenAddresses.length > 0) {
        console.log(`[PortfolioPreview] Looking up ${inkTokenAddresses.length} Ink token addresses`);
        
        try {
          const { data, error: supabaseError } = await supabase
            .from('tokens')
            .select('contract_address, logo_url, name, symbol')
            .eq('network', 'ink');
          
          if (!supabaseError && data) {
            console.log(`[PortfolioPreview] Fetched ${data.length} total Ink tokens from Supabase`);
            const addressSet = new Set(inkTokenAddresses);
            
            data.forEach(token => {
              const lowerAddr = token.contract_address.toLowerCase();
              if (addressSet.has(lowerAddr)) {
                tokenMap.set(lowerAddr, token);
                if (token.logo_url) {
                  console.log(`[PortfolioPreview] Matched Ink token ${token.symbol} with logo`);
                }
              }
            });
          }
        } catch (err) {
          console.warn('[PortfolioPreview] Failed to fetch Ink tokens:', err);
        }
      }
      
      // Fetch Ethereum tokens from tokens_usd1
      if (ethTokenAddresses.length > 0) {
        console.log(`[PortfolioPreview] Looking up ${ethTokenAddresses.length} Ethereum token addresses`);
        
        try {
          const { data, error: supabaseError } = await supabase
            .from('tokens_usd1')
            .select('contract_address, logo_url, name, symbol');
          
          if (!supabaseError && data) {
            console.log(`[PortfolioPreview] Fetched ${data.length} total ETH tokens from tokens_usd1`);
            const addressSet = new Set(ethTokenAddresses);
            
            data.forEach(token => {
              const lowerAddr = token.contract_address.toLowerCase();
              if (addressSet.has(lowerAddr)) {
                tokenMap.set(lowerAddr, token);
                if (token.logo_url) {
                  console.log(`[PortfolioPreview] Matched ETH token ${token.symbol} with logo`);
                }
              }
            });
          }
        } catch (err) {
          console.warn('[PortfolioPreview] Failed to fetch ETH tokens:', err);
        }
      }
      
      console.log(`[PortfolioPreview] Found ${tokenMap.size} tokens with logos in Supabase`);
      setSupabaseTokens(tokenMap);
    };
    
    fetchSupabaseLogos();
  }, [balances]);

  // Copy contract address to clipboard (with fallback for unfocused documents)
  const copyToClipboard = async (address: string) => {
    try {
      // Try the modern clipboard API first
      if (navigator.clipboard && document.hasFocus()) {
        await navigator.clipboard.writeText(address);
      } else {
        // Fallback: create a temporary textarea and use execCommand
        const textarea = document.createElement('textarea');
        textarea.value = address;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const { totalUSD, sortedTokens, chainCount } = useMemo(() => {
    if (!balances || balances.length === 0) {
      return { totalUSD: 0, sortedTokens: [], chainCount: 0 };
    }

    const total = balances.reduce((sum, token) => sum + (token.valueUSD || 0), 0);
    const sorted = [...balances].sort((a, b) => (b.valueUSD || 0) - (a.valueUSD || 0));
    const chains = new Set(balances.map(b => b.chainId));
    
    return { totalUSD: total, sortedTokens: sorted, chainCount: chains.size };
  }, [balances]);

  // Get logo for a token, preferring Supabase logo over the one from balance data
  const getTokenLogo = (token: typeof sortedTokens[0]): string | undefined => {
    // Check Supabase logos for Ink (57073) and Ethereum (1)
    if (token.tokenAddress && (token.chainId === 57073 || token.chainId === 1)) {
      const supabaseToken = supabaseTokens.get(token.tokenAddress.toLowerCase());
      if (supabaseToken?.logo_url) {
        return supabaseToken.logo_url;
      }
    }
    return token.logo;
  };

  return (
    <Card className={className}>
      <Header>
        <Title>Portfolio</Title>
        <ViewAllLink onClick={() => navigate('/profile')}>
          View all →
        </ViewAllLink>
      </Header>

      {!isConnected ? (
        <ConnectPrompt>
          <TotalBalance>$0.00</TotalBalance>
          <PortfolioChart currentValue={0} />
          <p>Connect wallet to view balance</p>
        </ConnectPrompt>
      ) : loading ? (
        <>
          <TotalBalance>Loading...</TotalBalance>
          <LoadingState>Fetching balances across chains...</LoadingState>
        </>
      ) : error ? (
        <>
          <TotalBalance>$0.00</TotalBalance>
          <PortfolioChart currentValue={0} />
          <EmptyState>Failed to load balances</EmptyState>
        </>
      ) : sortedTokens.length === 0 ? (
        <>
          <TotalBalance>$0.00</TotalBalance>
          <PortfolioChart currentValue={0} />
          <EmptyState>No assets found</EmptyState>
        </>
      ) : (
        <>
          <TotalBalance>{formatUSD(totalUSD)}</TotalBalance>
          
          {/* Portfolio Chart - Beholder-style sparkline */}
          <PortfolioChart 
            currentValue={totalUSD} 
            tokens={sortedTokens.map(t => ({
              symbol: t.symbol,
              valueUSD: t.valueUSD || 0,
              chainId: t.chainId,
            }))}
          />
          
          <BalanceSubtext>
            {sortedTokens.length} asset{sortedTokens.length !== 1 ? 's' : ''} across {chainCount} chain{chainCount !== 1 ? 's' : ''}
          </BalanceSubtext>
          
          <TokenList>
            {sortedTokens.map((token, index) => {
              const logo = getTokenLogo(token);
              const hasContract = token.tokenAddress !== null;
              
              return (
                <TokenRow key={`${token.chainId}-${token.tokenAddress || 'native'}-${index}`}>
                  <TokenIcon>
                    {logo ? (
                      <img 
                        src={logo} 
                        alt={token.symbol}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>
                        {token.symbol.slice(0, 2)}
                      </span>
                    )}
                  </TokenIcon>
                  <TokenInfo>
                    <TokenSymbol>
                      {token.symbol}
                      {hasContract && (
                        <ContractWrapper>
                          <ContractBadge 
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(token.tokenAddress!);
                            }}
                            title={`Copy: ${token.tokenAddress}`}
                          >
                            {formatContractAddress(token.tokenAddress)}
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" />
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                            </svg>
                          </ContractBadge>
                          {copiedAddress === token.tokenAddress && (
                            <CopiedTooltip>Copied!</CopiedTooltip>
                          )}
                        </ContractWrapper>
                      )}
                    </TokenSymbol>
                    <TokenChain>{token.chainName}</TokenChain>
                  </TokenInfo>
                  <TokenValue>
                    <TokenUSD>{formatUSD(token.valueUSD || 0)}</TokenUSD>
                    <TokenAmount>{formatAmount(token.balance)} {token.symbol}</TokenAmount>
                  </TokenValue>
                </TokenRow>
              );
            })}
          </TokenList>
        </>
      )}

      <FundingOptions>
        <FundingOption onClick={() => window.open('https://www.kraken.com', '_blank')}>
          <FundingIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          </FundingIcon>
          <FundingText>
            <FundingTitle>Buy Crypto: Kraken</FundingTitle>
            <FundingDesc>Purchase with card via Kraken</FundingDesc>
          </FundingText>
        </FundingOption>
        
        <FundingOption onClick={() => window.open('https://relay.link/onramp/ethereum', '_blank')}>
          <FundingIcon>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="1" y="4" width="22" height="16" rx="2"/>
              <line x1="1" y1="10" x2="23" y2="10"/>
            </svg>
          </FundingIcon>
          <FundingText>
            <FundingTitle>Buy Crypto: Relay</FundingTitle>
            <FundingDesc>Purchase with card via Relay</FundingDesc>
          </FundingText>
        </FundingOption>
      </FundingOptions>
    </Card>
  );
};

export default PortfolioPreview;
